/**
 * Switch Atom
 *
 * A toggle switch input element for boolean on/off states.
 */

'use client';

import * as SwitchPrimitives from '@radix-ui/react-switch';
import * as React from 'react';

import { cn } from '@/lib/utils';

interface SwitchProps extends Omit<React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>, 'onChange'> {
  size?: 'small' | 'large';
  title?: string;
  description?: string;
  onChange?: (value: boolean) => void;
}

const SwitchBase = React.forwardRef<
  React.ComponentRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & { size?: 'small' | 'large' }
>(({ className, size = 'small', ...props }, ref) => {
  const switchSize = () => {
    if (size === 'small') return 'h-5 w-9';
    if (size === 'large') return 'h-6 w-11';
  };

  const toggleSize = () => {
    if (size === 'small') return 'h-4 w-4';
    if (size === 'large') return 'h-5 w-5';
  };
  return (
    <SwitchPrimitives.Root
      className={cn(
        'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-zinc-200 dark:focus-visible:ring-zinc-300 dark:focus-visible:ring-offset-zinc-950 dark:data-[state=checked]:bg-zinc-50 dark:data-[state=unchecked]:bg-zinc-800',
        className,
        switchSize()
      )}
      {...props}
      ref={ref}>
      <SwitchPrimitives.Thumb
        className={cn(
          'pointer-events-none block rounded-full bg-white shadow-lg ring-0 transition-transform peer-disabled:bg-zinc-50 data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0 dark:bg-zinc-950',
          toggleSize()
        )}
      />
    </SwitchPrimitives.Root>
  );
});
SwitchBase.displayName = SwitchPrimitives.Root.displayName;

const Switch = ({
  size = 'small',
  title,
  description,
  disabled,
  required,
  onChange,
  checked,
  onCheckedChange,
  ...props
}: SwitchProps) => {
  const handleChange = (checked: boolean) => {
    if (onChange) onChange(checked);
    if (onCheckedChange) onCheckedChange(checked);
  };

  return (
    <div className='items-top flex gap-2'>
      <SwitchBase
        onCheckedChange={handleChange}
        checked={checked}
        required={required}
        disabled={disabled}
        size={size}
        {...props}
      />
      {(title || description) && (
        <div>
          <p className='font-medium max-sm:text-sm'>{title}</p>
          <p className='text-foreground max-sm:text-sm'>{description}</p>
        </div>
      )}
    </div>
  );
};

export { Switch, SwitchBase };
